import React, { useState, useEffect, useRef } from 'react';
import LottieIllustration from './LottieIllustration';
import { loadAnimation } from '../utils/loadAnimation';

interface FormData {
  serviceType: string;
  projectDetails: string;
  budget: string;
  timeline: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
}

interface ContactInfo {
  icon: string;
  title: string;
  details: string;
  action?: string;
}

const Contact: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [contactAnimation, setContactAnimation] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>({
    serviceType: '',
    projectDetails: '',
    budget: '',
    timeline: '',
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const serviceTypes = [
    'E-commerce Platform',
    'Mobile App Development',
    'Web Application',
    'AI Chatbot',
    'Digital Marketing',
    'Custom Software',
    'Consulting',
    'Other'
  ];

  const budgetRanges = [
    '$10,000 - $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000+',
    'Not sure yet'
  ];

  const timelineOptions = [
    'ASAP (Rush project)',
    '1-2 months',
    '3-6 months',
    '6+ months',
    'Flexible timeline'
  ];

  const contactInfo: ContactInfo[] = [
    {
      icon: '📧',
      title: 'Email Us',
      details: 'contact@jagathisolutions.com'
    },
    {
      icon: '📞',
      title: 'Call Us',
      details: '+91 8919192170'
    },
    {
      icon: '📍',
      title: 'Visit Us',
      details: 'Kotipalli Bus Stand, T-Nagar, Rajahmundry, Andhra Pradesh'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Component is visible
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const loadContactAnimation = async () => {
      try {
        const animation = await loadAnimation('/animations/Contact Us Character Animation.json');
        setContactAnimation(animation);
      } catch (error) {
        console.error('Error loading contact animation:', error);
      }
    };

    loadContactAnimation();
  }, []);

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<FormData> = {};

    switch (step) {
      case 1:
        if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';
        break;
      case 2:
        if (!formData.projectDetails.trim()) newErrors.projectDetails = 'Please describe your project';
        if (!formData.budget) newErrors.budget = 'Please select a budget range';
        if (!formData.timeline) newErrors.timeline = 'Please select a timeline';
        break;
      case 3:
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.company.trim()) newErrors.company = 'Company name is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };


  if (isSubmitted) {
    return (
      <section ref={sectionRef} id="contact" className="py-10 dark:bg-navy-light bg-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="glass rounded-2xl p-12">
            <div className="text-6xl mb-6">✅</div>
            <h2 className="text-3xl font-bold dark:text-white text-gray-900 mb-4">Thank You!</h2>
            <p className="text-xl dark:text-gray-300 text-gray-600 mb-8">
              Your message has been sent successfully. We'll get back to you within 24 hours.
            </p>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(1);
                setFormData({
                  serviceType: '',
                  projectDetails: '',
                  budget: '',
                  timeline: '',
                  name: '',
                  email: '',
                  company: '',
                  phone: '',
                  message: ''
                });
              }}
              className="px-8 py-3 bg-accent hover:bg-accent-dark text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Send Another Message
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id="contact" className="py-10 dark:bg-navy-light bg-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold dark:text-white text-gray-900 mb-6">
            Get In <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Touch</span>
          </h2>
          <p className="text-xl dark:text-gray-300 text-gray-600 max-w-3xl mx-auto">
            Ready to start your next project? Let's discuss how we can help bring your vision to life.
          </p>
        </div>

        {/* Animation and Contact Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Side - Animation */}
          <div className="order-2 lg:order-1">
            <div className="bg-gradient-to-br from-accent/5 to-accent-light/5 rounded-3xl p-8">
              {contactAnimation ? (
                <LottieIllustration
                  animationData={contactAnimation}
                  className="w-full h-full"
                  width="100%"
                  height="400px"
                  loop={true}
                  autoplay={true}
                />
              ) : (
                <div className="flex items-center justify-center h-[400px]">
                  <div className="text-center">
                    <div className="text-6xl mb-4">📞</div>
                    <div className="text-lg font-semibold dark:text-white text-gray-900 mb-2">Contact Us</div>
                    <div className="text-sm dark:text-gray-400 text-gray-500">Let's discuss your project</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Contact Info */}
          <div className="order-1 lg:order-2">
            <h3 className="text-3xl font-bold dark:text-white text-gray-900 mb-6">
              Let's <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Connect</span>
            </h3>
            <p className="text-lg dark:text-gray-300 text-gray-600 mb-8 leading-relaxed">
              Ready to start your next project? We're here to help bring your vision to life 
              with innovative solutions and expert guidance.
            </p>
            
            <div className="space-y-6">
              <h4 className="text-xl font-semibold dark:text-white text-gray-900 mb-4">Other ways to reach us</h4>
              
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="glass rounded-xl p-6 hover:bg-opacity-30 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-3xl group-hover:scale-110 transition-transform duration-300">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="dark:text-white text-gray-900 font-semibold mb-1">{info.title}</h4>
                      <p className="dark:text-gray-300 text-gray-600">{info.details}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
